import React from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import MathquillUIElement from 'mathquill-ui';

import { t } from '../../../../services/i18nService';
import IntervalSaver from '../../../../services/IntervalSaver';
import stringUtils from '../../../../qti/utils/StringUtils';
import a11yIIBScreenReaderService, {
    A11yForIIBScreenReaderIteration,
} from '../../../../services/a11yIIBScreenReaderService';
import scorableItemNumberingService from '../../../../services/questionView/ScorableItemNumberingService';
import { shouldMathEntryInputUpdate } from './textEntryUtils';
import styles from '../mathWorkingInteraction/mathWorking.scss';
import textEntryStyles from './textEntry.scss';

const MATH_INPUT_FIELD_WIDTH_MULTIPLIER = 6.76;
const MAX_CONTENT_WIDTH = 700;

class MathEntryInput extends React.Component {
    static propTypes = {
        callback: PropTypes.func.isRequired, // callback(responseIdentifier, [string])
        defaultValue: PropTypes.string.isRequired,
        handleInteractionReady: PropTypes.func.isRequired,
        isReadOnly: PropTypes.bool,
        model: PropTypes.object.isRequired, // model taken from responseModels related to this interaction
        onInstanceReady: PropTypes.func,
        size: PropTypes.number.isRequired,
        mathSymbolPalettePreset: PropTypes.object,
        questionModel: PropTypes.object,
    };

    constructor(props) {
        super(props);
        this.state = {
            isTooWide: false,
            isFocused: false,
        };
        this.smartSaver = new IntervalSaver(props.defaultValue, (value) => {
            props.callback(props.model.identifier, [value]);
        });
    }

    componentDidMount() {
        this.initMathquill();
    }

    shouldComponentUpdate(nextProps, nextState) {
        return shouldMathEntryInputUpdate(this.props, nextProps, this.state, nextState);
    }

    // eslint-disable-next-line camelcase,react/sort-comp
    UNSAFE_componentWillUpdate(nextProps) {
        if (this.props.isReadOnly !== nextProps.isReadOnly) {
            ReactDOM.unmountComponentAtNode(this.mathQuillTargetElement);
        }
    }

    componentDidUpdate(prevProps) {
        if (this.props.isReadOnly !== prevProps.isReadOnly) {
            this.initMathquill();
        }
    }

    componentWillUnmount() {
        this.smartSaver.saveAndStop();

        if (this.mathQuillTargetElement) {
            ReactDOM.unmountComponentAtNode(this.mathQuillTargetElement);
        }
    }

    initMathquill = () => {
        if (typeof this.props.onInstanceReady === 'function') {
            this.props.onInstanceReady(this);
        }

        const { isReadOnly = false } = this.props;
        const mathQuillInitText = this.props.defaultValue;
        const mathSymbolPalettePreset = this.props.mathSymbolPalettePreset;

        const isA11yIIBScreenReaderSupportedV3 = a11yIIBScreenReaderService
            .isA11yIterationForIIBScreenReaderSupported(A11yForIIBScreenReaderIteration.v3);

        if (isReadOnly) {
            ReactDOM.render(
                <MathquillUIElement
                    key={`mathquill-${this.props.model.identifier}`}
                    triggerOnClick={false}
                    isStaticMathElement
                    id={this.getMathEditableId()}
                    mathquillText={mathQuillInitText}
                    onInstanceReady={this.onMathquillUIElementInstanceReady}
                    isA11yIIBScreenReaderEnabled={isA11yIIBScreenReaderSupportedV3}
                />,
                this.mathQuillTargetElement
            );
            this.setMathInputSize();
        } else {
            const mathquillProps = {
                key: `mathquill-${this.props.model.identifier}`,
                triggerOnClick: false,
                disableMenuCache: true,
                id: this.getMathEditableId(),
                wrapper: this.mathQuillTargetElement,
                mathquillText: mathQuillInitText,
                mathEditableChangeCallback: this.onChange,
                onInstanceReady: this.onMathquillUIElementInstanceReady,
                menuData: mathSymbolPalettePreset,
                isA11yIIBScreenReaderEnabled: isA11yIIBScreenReaderSupportedV3,
            };

            // Only add focus/blur handlers when a11y v3 is enabled
            if (isA11yIIBScreenReaderSupportedV3) {
                mathquillProps.onFocusHandler = this.handleFocusAndNavigateToItem;
                mathquillProps.onBlurHandler = this.onBlurHandler;
            }

            ReactDOM.render(
                <MathquillUIElement {...mathquillProps} />,
                this.mathQuillTargetElement
            );
        }
    };

    onChange = () => {
        const value = this.getValue();
        const width = this.getRenderedContentWidth();
        this.setState({ isTooWide: width > MAX_CONTENT_WIDTH });
        this.smartSaver.setValue(value);
    };

    handleFocusAndNavigateToItem = () => {
        const { model, questionModel } = this.props;
        this.setState({ isFocused: true });
        if (scorableItemNumberingService && questionModel) {
            scorableItemNumberingService.navigateToScorableItem(
                questionModel.contentRevisionId,
                model.identifier,
                undefined,
                true
            );
        }
    };

    onBlurHandler = () => {
        this.setState({ isFocused: false });
        this.smartSaver.saveIfReady(0);
    };

    setValue = (value) => {
        if (this.mathquillUIElement) {
            this.mathquillUIElement.setMathText(value);
        }
    };

    getValue = () => {
        return this.mathquillUIElement ? this.mathquillUIElement.getLatex() : '';
    };

    getRenderedContentWidth = () => {
        return this.mathquillUIElement ? this.mathquillUIElement.getRenderedContentWidth() : 0;
    };

    getMathEditableId = () => {
        if (this.mathEditableId) {
        // use existing (cached) value of this.mathEditableId
        } else if (this.props.model.variant === 'math') {
            this.mathEditableId = stringUtils.getUID();
        } else {
            this.mathEditableId = `${this.props.model.target}_valueId`;
        }

        return this.mathEditableId;
    };

    onMathquillUIElementInstanceReady = (instance) => {
        this.mathquillUIElement = instance;
        this.setMathInputSize();
        this.props.handleInteractionReady(true);
    };

    setMathInputSize = () => {
        const { isReadOnly, size } = this.props;
        let widthStyle = 'initial';

        if (size) {
            const pixelWidth = Math.max(
                Math.min(
                    Math.floor(size * MATH_INPUT_FIELD_WIDTH_MULTIPLIER),
                    MAX_CONTENT_WIDTH
                ),
                Math.floor(5 * MATH_INPUT_FIELD_WIDTH_MULTIPLIER),
            );
            widthStyle = `${pixelWidth}px`;
        }

        if (isReadOnly) {
            this.mathQuillTargetElement.style.minWidth = widthStyle;
            return;
        }

        const editableElement = this.mathQuillTargetElement.querySelector('.mathquill-editable');
        if (editableElement) {
            editableElement.style.width = widthStyle;
        }
    };

    render() {
        const { isReadOnly = false } = this.props;
        if (isReadOnly) {
            const classNames = `${textEntryStyles.print} mathquill-ui-static`;
            return <span className={classNames} ref={(div) => { this.mathQuillTargetElement = div; }} />;
        }

        const { isTooWide } = this.state;
        const rootClassNames = `interaction mathquill-outer-wrapper ${isTooWide ? styles.isTooWide : ''}`;
        const isA11ySupported = a11yIIBScreenReaderService
            .isA11yIterationForIIBScreenReaderSupported(A11yForIIBScreenReaderIteration.v3);
        const labelMessage = isA11ySupported ? undefined : t('interactions.inputEntry.inputFieldAriaLabel');

        return (
            <span className={rootClassNames} aria-label={labelMessage} dir="ltr">
                {
                    isTooWide ?
                        <span className={styles.message}>
                            {t('interactions.mathWorking.tooLongWarning')}
                        </span> : null
                }
                <span
                    className="mathquill-ui-wrapper"
                    ref={(div) => { this.mathQuillTargetElement = div; }}
                />
            </span>
        );
    }
}

export default MathEntryInput;
